import {
  Accordion,
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  ScrollArea,
  Search,
  Spinner,
} from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllExpertReports, useExpertReportsDetails } from '@/entities/expert-report';
import { ReportSection } from './ReportSection';
import { blocksHaveMatchingRules } from './ruleSearch';
import type { RuleWithPath } from './types';

type AddRulesDialogProps = {
  linkedRuleIds: number[];
  onAddRules: (rules: RuleWithPath[]) => void;
};

export const AddRulesDialog: FC<AddRulesDialogProps> = ({ linkedRuleIds, onAddRules }) => {
  const { t } = useTranslation(['common', 'coaching']);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRules, setSelectedRules] = useState<Map<number, RuleWithPath>>(new Map());
  const [expandedReports, setExpandedReports] = useState<string[]>([]);

  // Выбирать правила можно у любого заключения, поэтому набор берётся целиком;
  // пока окно закрыто, запрашивать его незачем
  const { data: reports, isLoading } = useAllExpertReports({ isDeleted: false }, { enabled: isOpen });

  const linkedSet = useMemo(() => new Set(linkedRuleIds), [linkedRuleIds]);

  const searchLower = search.toLowerCase().trim();
  const isSearchApplied = searchLower.length > 0;

  const reportItems = useMemo(() => reports?.items ?? [], [reports?.items]);
  const reportIds = useMemo(() => reportItems.map((report) => report.id), [reportItems]);

  // Правила живут в карточке заключения, поэтому поиск по ним требует карточек всего набора:
  // иначе правила нераскрытых заключений поиску не видны. Пока поиска нет, карточки не нужны
  const { details, isLoading: isLoadingDetails } = useExpertReportsDetails(reportIds, {
    enabled: isOpen && isSearchApplied,
  });

  const shownReports = useMemo(() => {
    if (!isSearchApplied) return reportItems.map((report) => ({ report, contentSearch: '' }));

    return reportItems.flatMap((report) => {
      // Заключение, подошедшее своим названием, показывается целиком: отбирать в нём ещё
      // и правила тем же условием значило бы раскрывать найденное заключение пустым
      if (report.name.toLowerCase().includes(searchLower)) return [{ report, contentSearch: '' }];

      const reportDetail = details.get(report.id);
      if (reportDetail && blocksHaveMatchingRules(reportDetail.blocks, searchLower)) {
        return [{ report, contentSearch: searchLower }];
      }
      return [];
    });
  }, [reportItems, isSearchApplied, searchLower, details]);

  const handleToggleRule = useCallback((rule: RuleWithPath) => {
    setSelectedRules((prev) => {
      const next = new Map(prev);
      if (next.has(rule.id)) {
        next.delete(rule.id);
      } else {
        next.set(rule.id, rule);
      }
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    if (selectedRules.size === 0) return;
    onAddRules(Array.from(selectedRules.values()));
    setIsOpen(false);
    setSelectedRules(new Map());
    setSearch('');
    setExpandedReports([]);
  }, [selectedRules, onAddRules]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedRules(new Map());
      setSearch('');
      setExpandedReports([]);
    }
  }, []);

  const handleExpandedChange = useCallback((value: string[]) => {
    setExpandedReports(value);
  }, []);

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" icon={<PlusIcon />}>
          {t('coaching:linkExpertReportRules')}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-160">
        <DialogHeader>
          <DialogTitle>{t('coaching:selectReportRules')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Search value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common:search')} />

          <ScrollArea className="max-h-96">
            {isLoading && (
              <div className="flex justify-center py-4">
                <Spinner className="size-5" />
              </div>
            )}
            {shownReports.length > 0 && (
              <Accordion type="multiple" value={expandedReports} onValueChange={handleExpandedChange}>
                {shownReports.map(({ report, contentSearch }) => (
                  <ReportSection
                    key={report.id}
                    reportId={report.id}
                    reportName={report.name}
                    isExpanded={expandedReports.includes(String(report.id))}
                    contentSearch={contentSearch}
                    linkedSet={linkedSet}
                    selectedRules={selectedRules}
                    onToggleRule={handleToggleRule}
                  />
                ))}
              </Accordion>
            )}
            {/* Пока карточки набора не пришли, найденных заключений может быть меньше — до конца
                загрузки отбор не закончен, и о ненайденном говорить рано */}
            {isLoadingDetails && (
              <div className="flex justify-center py-4">
                <Spinner className="size-5" />
              </div>
            )}
            {!isLoading && !isLoadingDetails && shownReports.length === 0 && (
              <div className="text-muted-foreground p-4">
                {isSearchApplied ? t('coaching:noReportsBySearch') : t('coaching:noReports')}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleAdd} disabled={selectedRules.size === 0}>
            {t('common:add')} {selectedRules.size > 0 && `(${selectedRules.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
